import axios from "axios";
import { CampaignMessageDoc } from "./campaign.service";

export interface CampaignFunnelCustomField {
  id: string;
  label: string;
  value: string;
}

export interface CampaignFunnelStage {
  id: string;
  name: string;
  system?: boolean;
  createdAt: string;
}

export interface CampaignFunnelRecord {
  id: string;
  stageId: string;
  phone: string;
  contactId?: string;
  listId?: string;
  listName: string;
  waMessageId?: string | null;
  sourceStatus: string;
  sourceMessageId: string;
  customerName: string;
  dealValue: string;
  notes: string;
  customFields: CampaignFunnelCustomField[];
  createdAt: string;
  updatedAt: string;
}

export interface CampaignFunnelState {
  stages: CampaignFunnelStage[];
  records: CampaignFunnelRecord[];
}

const BASE_URL =
  process.env.REACT_APP_PYTHON_BACKEND_URL || "http://localhost:8000/";

const API_BASE = "api";

const api = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

const DEFAULT_STAGE_DEFS = [
  { id: "interested", name: "Interested", system: true },
  { id: "customers", name: "Customers", system: true },
];

const nowIso = () => new Date().toISOString();

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "stage";

export const createDefaultCampaignFunnelState = (): CampaignFunnelState => {
  const createdAt = nowIso();

  return {
    stages: DEFAULT_STAGE_DEFS.map((stage) => ({
      ...stage,
      createdAt,
    })),
    records: [],
  };
};

const sanitizeStage = (
  stage: Partial<CampaignFunnelStage> | null | undefined,
): CampaignFunnelStage | null => {
  if (!stage?.id || !stage?.name) {
    return null;
  }

  return {
    id: String(stage.id),
    name: String(stage.name),
    system: Boolean(stage.system),
    createdAt: stage.createdAt || nowIso(),
  };
};

const sanitizeCustomField = (
  field: Partial<CampaignFunnelCustomField> | null | undefined,
): CampaignFunnelCustomField | null => {
  if (!field?.id) {
    return null;
  }

  return {
    id: String(field.id),
    label: String(field.label || ""),
    value: String(field.value || ""),
  };
};

const sanitizeRecord = (
  record: Partial<CampaignFunnelRecord> | null | undefined,
  stageIds: Set<string>,
): CampaignFunnelRecord | null => {
  if (!record?.id || !record?.phone || !record?.stageId) {
    return null;
  }

  if (!stageIds.has(String(record.stageId))) {
    return null;
  }

  return {
    id: String(record.id),
    stageId: String(record.stageId),
    phone: String(record.phone),
    contactId: record.contactId ? String(record.contactId) : undefined,
    listId: record.listId ? String(record.listId) : undefined,
    listName: String(record.listName || ""),
    waMessageId: record.waMessageId ? String(record.waMessageId) : null,
    sourceStatus: String(record.sourceStatus || ""),
    sourceMessageId: String(record.sourceMessageId || record.id),
    customerName: String(record.customerName || ""),
    dealValue: String(record.dealValue || ""),
    notes: String(record.notes || ""),
    customFields: Array.isArray(record.customFields)
      ? record.customFields
          .map((field) => sanitizeCustomField(field))
          .filter((field): field is CampaignFunnelCustomField => Boolean(field))
      : [],
    createdAt: record.createdAt || nowIso(),
    updatedAt: record.updatedAt || nowIso(),
  };
};

export const normalizeCampaignFunnelState = (
  state: Partial<CampaignFunnelState> | null | undefined,
): CampaignFunnelState => {
  const defaultState = createDefaultCampaignFunnelState();
  const stageMap = new Map<string, CampaignFunnelStage>();

  defaultState.stages.forEach((stage) => {
    stageMap.set(stage.id, stage);
  });

  if (Array.isArray(state?.stages)) {
    (state?.stages ?? [])
      .map((stage) => sanitizeStage(stage))
      .filter((stage): stage is CampaignFunnelStage => Boolean(stage))
      .forEach((stage) => {
        stageMap.set(stage.id, stage);
      });
  }

  const stages = Array.from(stageMap.values());
  const stageIds = new Set(stages.map((stage) => stage.id));

  const records = Array.isArray(state?.records)
    ? (state?.records ?? [])
        .map((record) => sanitizeRecord(record, stageIds))
        .filter((record): record is CampaignFunnelRecord => Boolean(record))
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    : [];

  return { stages, records };
};

export const getCampaignFunnelState = async (
  uuid: string,
  campaignId: string,
): Promise<CampaignFunnelState> => {
  const res = await api.get(`${API_BASE}/campaigns/${campaignId}/funnel`, {
    params: { uuid },
  });
  return normalizeCampaignFunnelState(res.data);
};

export const saveCampaignFunnelState = async (
  uuid: string,
  campaignId: string,
  state: CampaignFunnelState,
): Promise<CampaignFunnelState> => {
  const res = await api.put(
    `${API_BASE}/campaigns/${campaignId}/funnel`,
    normalizeCampaignFunnelState(state),
    { params: { uuid } },
  );
  return normalizeCampaignFunnelState(res.data);
};

export const getCampaignFunnelRecordId = (message: CampaignMessageDoc) =>
  String(message.contactId || message.to);

export const createCampaignFunnelCustomField = (): CampaignFunnelCustomField => ({
  id: `field-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  label: "",
  value: "",
});

export const addCampaignFunnelStage = (
  state: CampaignFunnelState,
  stageName: string,
): { state: CampaignFunnelState; stageId: string | null } => {
  const trimmed = stageName.trim();
  if (!trimmed) {
    return { state, stageId: null };
  }

  const existingStage = state.stages.find(
    (stage) => stage.name.toLowerCase() === trimmed.toLowerCase(),
  );

  if (existingStage) {
    return { state, stageId: existingStage.id };
  }

  const baseId = slugify(trimmed);
  const existingStageIds = new Set(state.stages.map((stage) => stage.id));
  let stageId = baseId;
  let index = 2;

  while (existingStageIds.has(stageId)) {
    stageId = `${baseId}-${index}`;
    index += 1;
  }

  return {
    state: {
      ...state,
      stages: [
        ...state.stages,
        {
          id: stageId,
          name: trimmed,
          system: false,
          createdAt: nowIso(),
        },
      ],
    },
    stageId,
  };
};

export const assignCampaignLeadToStage = (
  state: CampaignFunnelState,
  message: CampaignMessageDoc,
  stageId: string,
): CampaignFunnelState => {
  if (!state.stages.some((stage) => stage.id === stageId)) {
    return state;
  }

  const recordId = getCampaignFunnelRecordId(message);
  const timestamp = nowIso();
  const existingRecord = state.records.find((record) => record.id === recordId);

  if (existingRecord) {
    return {
      ...state,
      records: state.records
        .map((record) =>
          record.id === recordId
            ? {
                ...record,
                stageId,
                phone: message.to,
                contactId: message.contactId,
                listId: message.listId,
                listName: message.listName || "",
                waMessageId: message.waMessageId || null,
                sourceStatus: message.status,
                sourceMessageId: message._id,
                customerName: record.customerName || message.contactName || "",
                updatedAt: timestamp,
              }
            : record,
        )
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    };
  }

  return {
    ...state,
    records: [
      {
        id: recordId,
        stageId,
        phone: message.to,
        contactId: message.contactId,
        listId: message.listId,
        listName: message.listName || "",
        waMessageId: message.waMessageId || null,
        sourceStatus: message.status,
        sourceMessageId: message._id,
        customerName: message.contactName || "",
        dealValue: "",
        notes: "",
        customFields: [],
        createdAt: timestamp,
        updatedAt: timestamp,
      },
      ...state.records,
    ],
  };
};

export const moveCampaignFunnelRecord = (
  state: CampaignFunnelState,
  recordId: string,
  stageId: string,
): CampaignFunnelState => {
  if (!state.stages.some((stage) => stage.id === stageId)) {
    return state;
  }

  const timestamp = nowIso();

  return {
    ...state,
    records: state.records
      .map((record) =>
        record.id === recordId
          ? {
              ...record,
              stageId,
              updatedAt: timestamp,
            }
          : record,
      )
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
  };
};

export const updateCampaignFunnelRecord = (
  state: CampaignFunnelState,
  recordId: string,
  patch: Partial<
    Pick<
      CampaignFunnelRecord,
      "customerName" | "dealValue" | "notes" | "customFields"
    >
  >,
): CampaignFunnelState => {
  const timestamp = nowIso();

  return {
    ...state,
    records: state.records
      .map((record) =>
        record.id === recordId
          ? {
              ...record,
              ...patch,
              updatedAt: timestamp,
            }
          : record,
      )
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
  };
};

export const removeCampaignFunnelRecord = (
  state: CampaignFunnelState,
  recordId: string,
): CampaignFunnelState => ({
  ...state,
  records: state.records.filter((record) => record.id !== recordId),
});
